import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Clock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';

interface HoursChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  onSuccess?: () => void;
}

interface UpdateScheduleResponse {
  id: number;
  version: number;
  days: Record<string, number>;
  weekly_total?: number;
}

interface WorkingHours {
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
}

interface HoursChangeData {
  working_hours: WorkingHours;
  reason?: string;
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
] as const;

export const HoursChangeModal: React.FC<HoursChangeModalProps> = ({
  isOpen,
  onClose,
  employeeId,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [formData, setFormData] = useState<HoursChangeData>({
    working_hours: {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    },
    reason: '',
  });

  const totalWeeklyHours = Object.values(formData.working_hours).reduce(
    (sum, hours) => sum + hours,
    0
  );

  // No date fetching/logic; schedule updates are immediate.

  const handleHoursChange = (day: keyof WorkingHours, value: string) => {
    const hours = parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [day]: hours,
      },
    }));
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleReasonChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      reason: value,
    }));
  };

  // No month selection; updates are immediate.

  const setStandardWeekdayHours = (hours: number) => {
    setFormData(prev => ({
      ...prev,
      working_hours: {
        monday: hours,
        tuesday: hours,
        wednesday: hours,
        thursday: hours,
        friday: hours,
        saturday: 0,
        sunday: 0,
      },
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (totalWeeklyHours === 0) {
      newErrors.push('Total weekly hours cannot be zero');
    }

    if (totalWeeklyHours > 168) {
      newErrors.push('Total weekly hours cannot exceed 168 (24 hours × 7 days)');
    }

    // Check individual day limits
    Object.entries(formData.working_hours).forEach(([day, hours]) => {
      if (hours < 0) {
        newErrors.push(`Hours for ${day} cannot be negative`);
      }
      if (hours > 24) {
        newErrors.push(`Hours for ${day} cannot exceed 24`);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      // Prepare payload expected by update schedule API (capitalized day names)
      const daysPayload = {
        Monday: formData.working_hours.monday || 0,
        Tuesday: formData.working_hours.tuesday || 0,
        Wednesday: formData.working_hours.wednesday || 0,
        Thursday: formData.working_hours.thursday || 0,
        Friday: formData.working_hours.friday || 0,
        Saturday: formData.working_hours.saturday || 0,
        Sunday: formData.working_hours.sunday || 0,
      };

      const response = await ApiService.request<UpdateScheduleResponse>(
        EndpointKeys.UPDATE_EMPLOYEE_SCHEDULE,
        {
          method: 'POST',
          urlParams: { employee_id: employeeId.toString() },
          body: {
            days: daysPayload,
            change_reason: formData.reason || 'Working hours updated',
          },
        }
      );

      if (response && response.id) {
        toast({
          title: 'Working Hours Updated',
          description: 'New schedule version created successfully',
        });
        onSuccess?.();
        onClose();
        // Reset form
        setFormData({
          working_hours: {
            monday: 0,
            tuesday: 0,
            wednesday: 0,
            thursday: 0,
            friday: 0,
            saturday: 0,
            sunday: 0,
          },
          reason: '',
        });
      } else {
        setErrors(['Failed to update working hours']);
      }
    } catch (error: any) {
      console.error('Hours change error:', error);
      setErrors([error.response?.data?.error || 'An unexpected error occurred']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setErrors([]);
      setFormData({
        working_hours: {
          monday: 0,
          tuesday: 0,
          wednesday: 0,
          thursday: 0,
          friday: 0,
          saturday: 0,
          sunday: 0,
        },
        reason: '',
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Change Working Hours</DialogTitle>
          <DialogDescription>
            Update the employee's working hours schedule. Changes apply immediately as a new schedule version.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Quick preset buttons */}
          <div className="space-y-2">
            <Label>Quick Presets</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setStandardWeekdayHours(8)}
                disabled={isSubmitting}
              >
                Full-time (8h/day)
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setStandardWeekdayHours(4)}
                disabled={isSubmitting}
              >
                Part-time (4h/day)
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setStandardWeekdayHours(0)}
                disabled={isSubmitting}
              >
                Clear All
              </Button>
            </div>
          </div>

          {/* Working hours grid */}
          <div className="space-y-3">
            <Label>Weekly Schedule</Label>
            <div className="grid grid-cols-2 gap-3">
              {DAYS_OF_WEEK.map(({ key, label }) => (
                <div key={key} className="flex items-center space-x-2">
                  <Label className="w-20 text-sm">{label}</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="24"
                    placeholder="0"
                    value={formData.working_hours[key] || ''}
                    onChange={(e) => handleHoursChange(key, e.target.value)}
                    disabled={isSubmitting}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">hours</span>
                </div>
              ))}
            </div>
          </div>

          {/* Weekly total display */}
          <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
            <Clock className="h-4 w-4" />
            <span className="font-medium">
              Total Weekly Hours: {totalWeeklyHours.toFixed(1)}
            </span>
            {totalWeeklyHours > 48 && (
              <span className="text-amber-600 text-sm">
                (Exceeds standard working time)
              </span>
            )}
          </div>

          {/* Date selection removed: changes apply immediately */}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (Optional)</Label>
            <Textarea
              id="reason"
              placeholder="e.g., Requested reduced hours, seasonal adjustment, new role requirements..."
              value={formData.reason}
              onChange={(e) => handleReasonChange(e.target.value)}
              disabled={isSubmitting}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Updating...' : 'Update Working Hours'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
